import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { useEffect, useMemo, useRef } from 'react';
// Import only the specific selector and action creator needed instead of the full namespace
import * as proposalActionCreators from '@makemydeal/dr-dash-store/actions/proposalActionCreators';
import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';
import { LoadingSection } from '@makemydeal/dr-shared-components';
import { Alert } from '@interstate/components/Alert';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useDebounce } from '../utils/useDebounce';
import { useContentSizedIframe } from '../utils/useContentSizedIframe';
import type { ScopeGridSelections } from '@makemydeal/dr-dash-store/types/types';

const SCOPE_CHANGE_DEBOUNCE_MS = 500;

export const PrintProposalPreview = ({
    proposalId,
    selectedScopeIds,
    scopeGridSelections
}: {
    proposalId?: string;
    selectedScopeIds?: string[];
    scopeGridSelections?: ScopeGridSelections;
}) => {
    const htmlContent = useSelector(proposalSelectors.getProposalPreview) as string;
    const isLoadingPreview = useSelector(proposalSelectors.getProposalPreviewLoading);
    const isLoadingPreviewError = useSelector(proposalSelectors.getProposalPreviewError);
    const dispatch = useDispatch();
    const theme = useInterstateTheme();
    const scopeIdsKey = selectedScopeIds?.join(',') ?? '';
    const gridSelectionsKey = useMemo(() => JSON.stringify(scopeGridSelections ?? {}), [scopeGridSelections]);
    const isInitialRender = useRef(true);
    const { isContentHeightUnknown, onIframeLoad, iframeSizingStyle } = useContentSizedIframe(htmlContent);

    // On error no iframe is rendered, so its height stays unknown — don't wait for it then.
    const isAwaitingPreview = !isLoadingPreviewError && isContentHeightUnknown;

    const [debouncedFetchPreview] = useDebounce(
        () => dispatch(proposalActionCreators.getProposalPreview(proposalId, selectedScopeIds, scopeGridSelections)),
        SCOPE_CHANGE_DEBOUNCE_MS,
        [dispatch, proposalId, scopeIdsKey, gridSelectionsKey]
    );

    useEffect(() => {
        dispatch(proposalActionCreators.getProposalPreview(proposalId, selectedScopeIds, scopeGridSelections));
    }, [proposalId, dispatch]);

    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        debouncedFetchPreview();
    }, [scopeIdsKey, gridSelectionsKey]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0, mb: 1 }}>
            <LoadingSection loading={isLoadingPreview || isAwaitingPreview} testId={'loading-proposal-preview-loader'} size={70} />

            <Box sx={{ marginBottom: '1rem' }}>
                <Typography variant="h4">Preview</Typography>
            </Box>

            {isLoadingPreviewError ? (
                <Box sx={{ width: '100%' }}>
                    <Alert type="error">Unable to show a preview for selected printing proposal.</Alert>
                </Box>
            ) : (
                <Box
                    sx={{
                        border: `1px solid ${theme.tokens.BaseColorGray100}`,
                        background: theme.tokens.BaseColorWhite
                    }}
                >
                    {!isLoadingPreview && (
                        <iframe
                            onLoad={onIframeLoad}
                            srcDoc={htmlContent}
                            style={{
                                display: 'block',
                                width: '100%',
                                border: 'none',
                                ...iframeSizingStyle
                            }}
                            title="Proposal Preview"
                            data-testid="proposal-preview-iframe"
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};
